import React from 'react';
import { InputText } from 'primereact/inputtext';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  testId?: string;
  invalid?: boolean;
  /** Validation message; when present the field is styled as invalid. */
  error?: string;
  /** Accepted for API compatibility (renders a trailing clear affordance via CSS). */
  clearable?: boolean;
  /** Leading PrimeIcons class, e.g. "pi pi-search". */
  icon?: string;
  /** Optional field label rendered above the input. When set, the validation
   *  message (`error`) is shown beneath the field. Omit it and the component
   *  renders exactly as before — a bare input. */
  label?: string;
  /** Marks the field required — renders a red asterisk after the label. */
  required?: boolean;
  /** Value-based change handler. */
  onChange?: (value: string) => void;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ testId, invalid, error, clearable, icon, className, onChange, style, label, required, ...props }, ref) => {
    const input = (
      <InputText
        ref={ref}
        data-testid={testId}
        // `p-inputtext-left-icon` (index.css) rather than an inline paddingLeft:
        // the global `.p-inputtext { padding: … !important }` rule outranks
        // inline styles, so the icon would otherwise sit on top of the text.
        className={`${className || ''} ${icon ? 'p-inputtext-left-icon' : ''} ${invalid || error ? 'p-invalid' : ''}`}
        onChange={(e) => onChange?.(e.target.value)}
        style={style}
        {...(props as any)}
      />
    );

    /* Positioned here rather than via `p-input-icon-left`: that class was
       deprecated in PrimeReact 10 and no longer offsets the icon, so it rendered
       on top of the placeholder text. */
    const control = !icon ? input : (
      <span className="relative inline-flex w-full items-center">
        <i
          className={`${icon} pointer-events-none absolute left-3 text-gray-400 dark:text-gray-500`}
          style={{ fontSize: '0.8rem' }}
        />
        {input}
      </span>
    );

    if (!label) {
      return control;
    }

    return (
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {label}{required && <span className="ml-1 text-red-500">*</span>}
        </label>
        {control}
        {error && <small className="text-rose-600 dark:text-rose-400">{error}</small>}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;
